/**
 * Where checkpoints live — one interface, two adapters.
 *
 * ===========================================================================
 * WHY THE INTERFACE IS ASYNC AND THE FILE STORE IS TOO
 * ===========================================================================
 *
 * IndexedDB has no synchronous API, so `CheckpointStore` has to be async. The
 * Node adapter is therefore async as well even though `fs` offers a blocking
 * form: two adapters with the same shape means the trainer, the CLI and the UI
 * all drive one code path, and the alternative — a sync store plus an async one
 * — puts the difference into every caller.
 *
 * ===========================================================================
 * NEITHER ADAPTER TOUCHES ANYTHING AT MODULE SCOPE
 * ===========================================================================
 *
 * This module is imported by the browser UI AND by the Node CLI, so it must
 * load cleanly in both. `createIndexedDbStore` reads `indexedDB` only when one
 * of its methods is called, and `createFileStore` reaches `node:fs/promises`
 * through a specifier the bundler cannot follow. Importing this file in Node
 * must not throw, and `test/rl/storage.test.ts` asserts exactly that — the
 * failure it prevents is a training UI that cannot even be bundled.
 *
 * ===========================================================================
 * A LIST MUST NOT READ EVERY CHECKPOINT
 * ===========================================================================
 *
 * A checkpoint of a [256, 256] network with its Adam moments is on the order of
 * six megabytes of JSON. A `list()` that parsed twenty of them to show twenty
 * rows would stall for seconds and allocate hundreds of megabytes. The file
 * store therefore writes a small `<id>.meta.json` beside each checkpoint and
 * lists from those, falling back to the full file only when the sidecar is
 * missing (a checkpoint copied in by hand still lists).
 *
 * Writes go to a temporary file and are renamed into place. A run left going
 * for hours is going to be interrupted eventually, and rename is the only step
 * of a save that is atomic — without it an interrupted save leaves a truncated
 * JSON file where the newest checkpoint should be.
 */

import type { Checkpoint } from './types';

// ---------------------------------------------------------------------------
// The interface
// ---------------------------------------------------------------------------

export interface CheckpointMeta {
  id: string;
  label?: string;
  iteration: number;
  totalSteps: number;
  /** Unix milliseconds. The one wall-clock read in this file. */
  savedAt: number;
}

export interface CheckpointStore {
  /** Newest first. */
  list(): Promise<CheckpointMeta[]>;
  load(id: string): Promise<Checkpoint>;
  save(id: string, cp: Checkpoint): Promise<void>;
  delete(id: string): Promise<void>;
}

/** Ids become file names and IndexedDB keys, so they are constrained rather
 *  than escaped: an id containing a path separator is a caller bug, and
 *  silently rewriting it would make `save(id)` and `load(id)` disagree. */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

const assertId = (id: string): string => {
  if (!ID_PATTERN.test(id) || id.includes('..')) {
    throw new Error(
      `checkpoint id ${JSON.stringify(id)} must match ${String(ID_PATTERN)} and contain no '..'`
    );
  }
  return id;
};

/** Enough of a check to catch a wrong file, without deciding whether THIS build
 *  can run it — that judgement belongs to ./policy, which can say why. */
const asCheckpoint = (value: unknown, where: string): Checkpoint => {
  if (
    typeof value !== 'object' ||
    value === null ||
    typeof (value as Checkpoint).version !== 'number' ||
    !Array.isArray((value as Checkpoint).parameters)
  ) {
    throw new Error(`${where} does not contain a checkpoint`);
  }
  return value as Checkpoint;
};

const metaOf = (id: string, cp: Checkpoint, savedAt: number): CheckpointMeta => ({
  id,
  label: cp.label,
  iteration: cp.stats?.iteration ?? 0,
  totalSteps: cp.stats?.totalSteps ?? 0,
  savedAt,
});

const newestFirst = (a: CheckpointMeta, b: CheckpointMeta): number => b.savedAt - a.savedAt;

// ---------------------------------------------------------------------------
// Node: one JSON file per checkpoint
// ---------------------------------------------------------------------------

/** See `nodeImport` in ./trainer — same escape hatch, same reason. */
const nodeImport = <T>(specifier: string): Promise<T> =>
  import(/* @vite-ignore */ specifier) as Promise<T>;

type NodeFs = typeof import('node:fs/promises');
type NodePath = typeof import('node:path');

const META_SUFFIX = '.meta.json';
const DATA_SUFFIX = '.json';

export const createFileStore = (dir: string): CheckpointStore => {
  const modules = async (): Promise<{ fs: NodeFs; path: NodePath }> => {
    const [fs, path] = await Promise.all([
      nodeImport<NodeFs>('node:fs/promises'),
      nodeImport<NodePath>('node:path'),
    ]);
    return { fs, path };
  };

  const readJson = async (fs: NodeFs, file: string): Promise<unknown> =>
    JSON.parse(await fs.readFile(file, 'utf8')) as unknown;

  return {
    async list(): Promise<CheckpointMeta[]> {
      const { fs, path } = await modules();
      let entries: string[];
      try {
        entries = await fs.readdir(dir);
      } catch {
        // A directory that does not exist yet holds no checkpoints. That is a
        // normal first run, not an error worth propagating to a UI.
        return [];
      }

      const out: CheckpointMeta[] = [];
      for (const entry of entries) {
        if (!entry.endsWith(DATA_SUFFIX) || entry.endsWith(META_SUFFIX)) continue;
        const id = entry.slice(0, -DATA_SUFFIX.length);
        try {
          const sidecar = await readJson(fs, path.join(dir, id + META_SUFFIX));
          out.push({ ...(sidecar as CheckpointMeta), id });
        } catch {
          try {
            const cp = asCheckpoint(await readJson(fs, path.join(dir, entry)), entry);
            const stat = await fs.stat(path.join(dir, entry));
            out.push(metaOf(id, cp, stat.mtimeMs));
          } catch {
            // Not a checkpoint. Listing a directory must not fail because
            // something unrelated is sitting in it.
          }
        }
      }
      return out.sort(newestFirst);
    },

    async load(id: string): Promise<Checkpoint> {
      const { fs, path } = await modules();
      const file = path.join(dir, assertId(id) + DATA_SUFFIX);
      return asCheckpoint(await readJson(fs, file), file);
    },

    async save(id: string, cp: Checkpoint): Promise<void> {
      const { fs, path } = await modules();
      assertId(id);
      await fs.mkdir(dir, { recursive: true });
      const meta = metaOf(id, cp, Date.now());

      const write = async (name: string, value: unknown): Promise<void> => {
        const target = path.join(dir, name);
        const temp = `${target}.tmp`;
        await fs.writeFile(temp, JSON.stringify(value), 'utf8');
        await fs.rename(temp, target);
      };
      await write(id + DATA_SUFFIX, cp);
      await write(id + META_SUFFIX, meta);
    },

    async delete(id: string): Promise<void> {
      const { fs, path } = await modules();
      assertId(id);
      for (const suffix of [DATA_SUFFIX, META_SUFFIX]) {
        await fs.rm(path.join(dir, id + suffix), { force: true });
      }
    },
  };
};

// ---------------------------------------------------------------------------
// Browser: one IndexedDB object store
// ---------------------------------------------------------------------------

/** Object store name. Records are `{ id, meta, checkpoint }`, so `list()` can
 *  read the small half of each record without materialising the parameters. */
const STORE = 'checkpoints';

interface DbRecord {
  id: string;
  meta: CheckpointMeta;
  checkpoint: Checkpoint;
}

const wrap = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });

/** Checkpoint database under the game's old name, from before the rename. */
export const LEGACY_CHECKPOINT_DB = 'rocketweb-rl';

/**
 * The database name to actually open: `preferred`, unless the only one that
 * exists is `legacy`.
 *
 * A player who trained a bot before the rename has their checkpoints in a
 * database named after the old game. Opening the new name would show them an
 * empty checkpoint list — hours of training apparently gone, with the data
 * still sitting there under a name nothing asks for. Preferring the existing
 * database keeps them, and copying nothing keeps this to a name lookup: no
 * write, no delete, and no half-finished migration to recover from.
 *
 * `databases()` is Chrome-and-friends only. Where it is missing the answer is
 * `preferred` — a browser that cannot be asked must not have a database
 * conjured into existence just to find out it is empty.
 */
export const resolveCheckpointDbName = async (
  factory: IDBFactory,
  preferred: string,
  legacy: string = LEGACY_CHECKPOINT_DB
): Promise<string> => {
  if (preferred === legacy) return preferred;
  try {
    const list = await factory.databases?.();
    if (list === undefined) return preferred;
    const named = (want: string): boolean => list.some(entry => entry.name === want);
    return !named(preferred) && named(legacy) ? legacy : preferred;
  } catch {
    // Firefox throws rather than omitting the method, and a private window can
    // refuse outright. Either way the new name is the safe answer.
    return preferred;
  }
};

const openNamed = (factory: IDBFactory, dbName: string): Promise<IDBDatabase> =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(dbName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(`cannot open ${dbName}`));
  });

export const createIndexedDbStore = (dbName = 'vroomball-rl'): CheckpointStore => {
  let handle: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (handle !== null) return handle;
    handle = (async (): Promise<IDBDatabase> => {
      const factory = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
      if (factory === undefined) {
        throw new Error('IndexedDB is not available; use createFileStore in Node');
      }
      return openNamed(factory, await resolveCheckpointDbName(factory, dbName));
    })();
    // A rejected handle must not be cached, or one failed open (a private
    // window, a blocked upgrade) would poison every later call for the tab.
    handle.catch(() => {
      handle = null;
    });
    return handle;
  };

  const transact = async <T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore) => Promise<T>
  ): Promise<T> => {
    const db = await open();
    const tx = db.transaction(STORE, mode);
    const result = await body(tx.objectStore(STORE));
    // Resolve on the TRANSACTION, not on the request: a write whose request has
    // succeeded is still not durable until the transaction commits, and a save
    // that resolved early would let the caller exit the process first.
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
    return result;
  };

  return {
    async list(): Promise<CheckpointMeta[]> {
      const records = await transact('readonly', (store) => wrap(store.getAll() as IDBRequest<DbRecord[]>));
      return records.map((record) => ({ ...record.meta, id: record.id })).sort(newestFirst);
    },

    async load(id: string): Promise<Checkpoint> {
      const record = await transact('readonly', (store) =>
        wrap(store.get(assertId(id)) as IDBRequest<DbRecord | undefined>)
      );
      if (record === undefined) throw new Error(`no checkpoint ${JSON.stringify(id)}`);
      return asCheckpoint(record.checkpoint, id);
    },

    async save(id: string, cp: Checkpoint): Promise<void> {
      assertId(id);
      const record: DbRecord = { id, meta: metaOf(id, cp, Date.now()), checkpoint: cp };
      await transact('readwrite', (store) => wrap(store.put(record)));
    },

    async delete(id: string): Promise<void> {
      assertId(id);
      await transact('readwrite', (store) => wrap(store.delete(id)));
    },
  };
};
